import { AngleConfiguration, Configuration, FlowRateConfiguration, PressureConfiguration } from "../types/sharedTypes";

export class ConfigurationService {

    protected angles: Array<AngleConfiguration> = [];
    protected flowRates: Array<FlowRateConfiguration> = [];
    protected pressures: Array<PressureConfiguration> = [];

    constructor(
        configuration: Configuration,
    ) {
        // Make sure that angles, flowRates and pressures are sorted by their respective primary value,
        // the rest of the functionality depends on it.

        this.angles = [...configuration.angles ?? []].sort((a, b) => a.angle - b.angle);
        this.flowRates = [...configuration.flowRates ?? []].sort((a, b) => a.volume - b.volume);
        this.pressures = [...configuration.pressures ?? []].sort((a, b) => a.pressure - b.pressure);
    }

    public findAngleConfigurationForGivenAngle(givenAngle: number): AngleConfiguration | null {
        return ConfigurationService.selectConfigurationValue({
            needle: givenAngle,
            sortedHaystack: this.angles,
            accessor: e => e.angle,
        });
    }

    public findFlowRateConfigurationForGivenFlowRate(givenFlowRate: number): FlowRateConfiguration | null {
        return ConfigurationService.selectConfigurationValue({
            needle: givenFlowRate,
            sortedHaystack: this.flowRates,
            accessor: e => e.volume,
        });
    }

    public findPressureConfigurationForGivenPressure(givenPressure: number): PressureConfiguration | null {
        return ConfigurationService.selectConfigurationValue({
            needle: givenPressure,
            sortedHaystack: this.pressures,
            accessor: e => e.pressure,
        });
    }

    public static selectConfigurationValue<T>(
        {
            accessor,
            needle,
            sortedHaystack,
        }: {

            needle: number,
            sortedHaystack: Array<T> | null,
            accessor: (param: T) => number,
        }
    ): T | null {
        if (!sortedHaystack || !sortedHaystack.length) {
            return null;
        }

        // If an exact match is found: return it.
        const exactMatch = sortedHaystack.find(e => accessor(e) === needle);
        if (exactMatch) {
            return exactMatch;
        }

        // Find the index of the first haystack entry which has a value greater than needle.
        const firstGreaterMatchIndex = sortedHaystack.findIndex(e => accessor(e) > needle);

        // Edge Case: Needle is greater then the greatest haystack entry.
        // Return the last (greatest) haystack entry.
        if (firstGreaterMatchIndex === -1) {
            return sortedHaystack.at(-1) ?? null;
        }

        // Edge Case: Needle is less then the smallest haystack entry.
        // Return the first (lowest) haystack entry.
        if (firstGreaterMatchIndex === 0) {
            return sortedHaystack[0];
        }

        // Default Case: Needle is between any two configuration entry.
        // Return the element BEFORE the matched index, we are "rounding down" by default.
        return sortedHaystack[firstGreaterMatchIndex - 1];
    }
}